import { useEffect, useRef, useState } from "react"
import { FieldValues } from "react-hook-form"

import { useEvent } from "shared/lib/useEvent"
import { ApiResponse } from "shared/types/ApiResponse"
import { FormRef } from "shared/types/ReactHookForm"

export interface UseCustomFormSubmitProps<
  TFieldValues extends FieldValues = FieldValues
> {
  formRef?: FormRef<TFieldValues>
  onSuccess?: (data: TFieldValues) => void
  onError?: (error: TFieldValues) => void
  onSubmit: (data: TFieldValues) => Promise<ApiResponse<unknown>> | void
}

export function useFormSubmit<TFieldValues extends FieldValues = FieldValues>({
  onSubmit,
  onSuccess,
  onError,
  formRef
}: UseCustomFormSubmitProps<TFieldValues>): {
  isSubmitting: boolean
  handleSubmit: (data: TFieldValues) => void
} {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  const handleSubmit = useEvent((data: TFieldValues) => {
    const promise = onSubmit(data)
    if (promise) {
      setIsSubmitting(true)
      promise
        .then(result => {
          if (!isMounted.current) return
          if (!result.errors?.length) {
            // Only reset if formRef is a ref object (not a callback function)
            if (formRef && typeof formRef !== "function") {
              formRef.current?.reset()
            }
            onSuccess?.(data)
          } else {
            onError?.(data)
          }
        })
        .finally(() => {
          if (isMounted.current) setIsSubmitting(false)
        })
    }
  })

  return {
    isSubmitting,
    handleSubmit
  }
}
